export type ThemeMode = 'dark' | 'light';

export interface ViewportTheme {
  clearColor: number;
  gridColor: number;
  gridTextColor: string;
  gridCmBorderColor: string;
  gridLabelStrokeColor: string;
  outlineVisibleColor: number;
  outlineHiddenColor: number;
  viewCubeFaceColors: number[];
  viewCubeTextColor: string;
  viewCubeEdgeColor: number;
  viewCubeEdgeOpacity: number;
  dimensionLineColor: number;
  dimensionArrowColor: number;
}

const DARK_VIEWPORT: ViewportTheme = {
  clearColor: 0x0a0b0d,
  gridColor: 0x5a6270,
  gridTextColor: '#c9ccd1',
  gridCmBorderColor: 'rgba(201,204,209,',   // will append alpha)
  gridLabelStrokeColor: 'rgba(10,11,13,0.8)',
  outlineVisibleColor: 0x000000,
  outlineHiddenColor: 0x222222,
  viewCubeFaceColors: [0x2d323a, 0x2d323a, 0x3a4049, 0x23272e, 0x2d323a, 0x2d323a],
  viewCubeTextColor: '#e8e9eb',
  viewCubeEdgeColor: 0xc9ccd1,
  viewCubeEdgeOpacity: 0.4,
  dimensionLineColor: 0xe6b54e,  // amber — visible on dark bg, matches accent
  dimensionArrowColor: 0xe6b54e,
};

const LIGHT_VIEWPORT: ViewportTheme = {
  clearColor: 0xf5f3ee,
  gridColor: 0x615e54,
  gridTextColor: '#2c2c28',
  gridCmBorderColor: 'rgba(44,44,40,',
  gridLabelStrokeColor: 'rgba(245,243,238,0.9)',
  outlineVisibleColor: 0x333333,
  outlineHiddenColor: 0xaaaaaa,
  viewCubeFaceColors: [0xe2e0d7, 0xe2e0d7, 0xecebe4, 0xd8d5ca, 0xe2e0d7, 0xe2e0d7],
  viewCubeTextColor: '#1a1a18',
  viewCubeEdgeColor: 0x8a8578,
  viewCubeEdgeOpacity: 0.5,
  dimensionLineColor: 0xb85c2a,  // rust-amber for light parchment
  dimensionArrowColor: 0xb85c2a,
};

const STORAGE_KEY = 'scadplus-theme';

export function getCurrentTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch { /* ignore */ }
  return 'dark';
}

export function setTheme(mode: ThemeMode): void {
  document.documentElement.dataset.theme = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('themechange', { detail: mode }));
}

export function toggleTheme(): ThemeMode {
  const next = getCurrentTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

export function getViewportTheme(mode: ThemeMode): ViewportTheme {
  return mode === 'light' ? LIGHT_VIEWPORT : DARK_VIEWPORT;
}
